/**
 * homeMessages.js - 홈 페이지 UI 메시지
 *
 * AICC 메인 홈 화면의 환영 메시지, 바로가기, KPI 지표, 공지사항 등 한글 UI 텍스트
 *
 * @see HomePage
 */

// ============================================
// 홈페이지 메시지 및 데이터 상수
// ============================================
export const HOME_TEXT = {
  // 페이지 제목
  PAGE_TITLE: 'XYRO PLUG SONA',

  // 환영 메시지
  WELCOME_TITLE: '안녕하세요',
  WELCOME_SUFFIX: '님!',
  WELCOME_SUBTITLE: '엔터프라이즈 AI 상담 · 워크플로우 자동화 플랫폼',
  WELCOME_GUEST: '게스트',

  // 섹션 제목
  SECTION_QUICK_ACCESS: '바로가기',
  SECTION_WORKSPACE: '워크스페이스',
  SECTION_ANNOUNCEMENTS: '공지사항',
  SECTION_GETTING_STARTED: '시작 가이드',

  // 새 섹션 제목 및 라벨
  SECTION_KPI: '핵심 지표',
  SECTION_LIVE_ACTIVITY: '실시간 활동',
  LABEL_VIEW_ALL: '전체 보기',
  LABEL_VIEW_ANALYSIS: '분석 보기',
  LABEL_CHANNEL_STATUS: '채널 상태',
  LABEL_NO_ACTIVITY: '최근 활동 내역이 없습니다',
  LABEL_LOADING: '로딩 중...',
};

/**
 * Quick Access Hero 카드 (가장 자주 쓰는 4개 기능)
 */
export const QUICK_ACCESS_ITEMS = [
  {
    key: 'chat',
    label: '모델 플레이그라운드',
    description: 'AI 모델과 대화하기',
    path: '/chat',
    icon: 'chat',
  },
  {
    key: 'workflow-editor',
    label: '워크플로우 에디터',
    description: '워크플로우 설계하기',
    path: '/workflow-editor',
    icon: 'editor',
  },
  {
    key: 'workflow-test',
    label: '워크플로우 테스터',
    description: '워크플로우 실행하기',
    path: '/workflow-test',
    icon: 'play',
  },
  {
    key: 'tools',
    label: '도구 관리',
    description: 'Function Calling 도구',
    path: '/tool-manager',
    icon: 'tool',
  },
];

/**
 * Workspace 카테고리 (Nav 드롭다운과 1:1 매칭)
 * minRole: 해당 카테고리/항목을 볼 수 있는 최소 역할
 */
export const WORKSPACE_CATEGORIES = [
  {
    key: 'builder',
    title: '설계',
    description: 'AI 워크플로우 설계 및 구성',
    minRole: 'USER',
    items: [
      { label: '워크플로우', path: '/workflow-editor', minRole: 'USER' },
      { label: '프롬프트 관리', path: '/admin/system-prompts', minRole: 'TENANT_ADMIN' },
      { label: '도구 관리', path: '/tool-manager', minRole: 'USER' },
      { label: '변수 관리', path: '/variable-manager', minRole: 'USER' },
    ],
  },
  {
    key: 'labs',
    title: '테스트',
    description: 'AI 모델 테스트 및 실험',
    minRole: 'USER',
    items: [
      { label: 'AI 대화', path: '/chat', minRole: 'USER' },
      { label: '음성 합성 테스트', path: '/admin/chat-tts', minRole: 'TENANT_ADMIN' },
      { label: '워크플로우 테스터', path: '/workflow-test', minRole: 'USER' },
      { label: '상담 시뮬레이터', path: '/orchestrator-chat', minRole: 'USER' },
    ],
  },
  {
    key: 'monitoring',
    title: '모니터링',
    description: '실시간 모니터링 및 분석',
    minRole: 'TENANT_ADMIN',
    items: [
      { label: '대시보드', path: '/admin', minRole: 'TENANT_ADMIN' },
      { label: '실시간 현황판', path: '/admin/wallboard', minRole: 'TENANT_ADMIN' },
      { label: '실시간 세션', path: '/admin/sessions', minRole: 'TENANT_ADMIN' },
      { label: '대화 로그', path: '/admin/conversations', minRole: 'TENANT_ADMIN' },
      { label: '서비스 분석', path: '/admin/service-analytics', minRole: 'TENANT_ADMIN' },
      { label: '채널 분석', path: '/admin/channels', minRole: 'TENANT_ADMIN' },
    ],
  },
  {
    key: 'admin',
    title: '관리',
    description: '테넌트, 사용자 및 시스템 관리',
    minRole: 'TENANT_ADMIN',
    items: [
      { label: '테넌트 · 채널', path: '/admin/tenants', minRole: 'TENANT_ADMIN' },
      { label: '공지사항', path: '/admin/announcements', minRole: 'TENANT_ADMIN' },
      { label: '사용자 관리', path: '/admin/users', minRole: 'GLOBAL_ADMIN' },
      { label: '권한 관리', path: '/admin/permissions', minRole: 'GLOBAL_ADMIN' },
      { label: 'AI 모델 관리', path: '/admin/llm-models', minRole: 'GLOBAL_ADMIN' },
      { label: '감사 로그', path: '/admin/audit-logs', minRole: 'GLOBAL_ADMIN' },
    ],
  },
];

/**
 * 빠른 시작 가이드
 */
export const GETTING_STARTED_GUIDES = [
  {
    id: 1,
    step: '01',
    title: 'AI 모델과 대화하기',
    description: '다양한 LLM 모델과 직접 대화를 시작해 보세요.',
    path: '/chat',
  },
  {
    id: 2,
    step: '02',
    title: '워크플로우 만들기',
    description: '비주얼 에디터로 AI 워크플로우를 설계하세요.',
    path: '/workflow-editor',
  },
  {
    id: 3,
    step: '03',
    title: '도구 등록하기',
    description: 'AI가 호출할 수 있는 도구를 등록하고 관리하세요.',
    path: '/tool-manager',
  },
  {
    id: 4,
    step: '04',
    title: '워크플로우 테스트',
    description: '만든 워크플로우를 실행하고 결과를 확인하세요.',
    path: '/workflow-test',
  },
];

/**
 * 공지사항 데이터
 * 추후 API에서 가져올 수 있도록 구조화
 */
export const ANNOUNCEMENTS = [
  {
    id: 1,
    type: 'feature', // feature, update, notice, maintenance
    title: '워크플로우 에디터 기능 추가',
    content: 'React Flow 기반의 비주얼 워크플로우 에디터가 추가되었습니다.',
    date: '2025-01-10',
    isNew: true,
  },
  {
    id: 2,
    type: 'update',
    title: 'LLM Chat 성능 개선',
    content: '스트리밍 응답 속도가 개선되었습니다.',
    date: '2025-01-08',
    isNew: false,
  },
  {
    id: 3,
    type: 'notice',
    title: '시스템 이용 안내',
    content: '원활한 서비스 이용을 위해 최신 브라우저를 사용해 주세요.',
    date: '2025-01-05',
    isNew: false,
  },
];

/**
 * 공지사항 타입별 라벨
 */
export const ANNOUNCEMENT_TYPE_LABELS = {
  info: '안내',
  warning: '주의',
  important: '중요',
};

/**
 * 공지사항 타입별 색상 클래스
 */
/**
 * Quick Action 버튼 목록 (역할별 필터)
 */
export const QUICK_ACTION_ITEMS = [
  { key: 'chat', label: 'AI 대화 시작', path: '/chat', minRole: 'USER' },
  { key: 'workflow-create', label: '워크플로우 만들기', path: '/workflow-editor', minRole: 'USER' },
  { key: 'workflow-test', label: '워크플로우 테스트', path: '/workflow-test', minRole: 'USER' },
  { key: 'tools', label: '도구 등록', path: '/tool-manager', minRole: 'USER' },
  { key: 'wallboard', label: '실시간 현황판', path: '/admin/wallboard', minRole: 'TENANT_ADMIN' },
  {
    key: 'prompts',
    label: 'Prompt Studio',
    path: '/admin/system-prompts',
    minRole: 'TENANT_ADMIN',
  },
  { key: 'tenants', label: '테넌트 관리', path: '/admin/tenants', minRole: 'GLOBAL_ADMIN' },
  { key: 'users', label: '사용자 관리', path: '/admin/users', minRole: 'GLOBAL_ADMIN' },
];

export const ANNOUNCEMENT_TYPE_CLASSES = {
  info: 'badge-info',
  warning: 'badge-warning',
  important: 'badge-important',
};
